import Vue from 'vue'
import fb from '../../firebase'
import {
  OpsMonitorState,
  RootState,
  FirestoreDocSnapper,
  FirestoreQuerySnapper,
  FirestoreSnapperType,
  ArchiverTask
} from '../types'
import { ActionContext } from 'vuex'
import store, { moduleActionContext, storehelper } from '..'
import { lazyUpdate } from '@/store/storehelper'
import { firestore } from 'firebase/app'
import { BackupOverview, CefStat, CefStatDetail, SocialMediaStats,
  TranscodingMachine, TranscodingStats, NGCVPMachineStat, GlobalAccelerator } from '@/types/ops'
import { Cast, CastStatus } from '@/types/casts'
import { Stream } from '@/types/streams'
import { UserProfile } from '@/types/users'
import { WowzaStat } from '@/types/wowza'
import { CollectionNames, KeyDict } from '@/types'

type OpsMonitorContext = ActionContext<OpsMonitorState, RootState>

const OpsMonitorModule = {
  namespaced: true,
  state: {
    pendingSupportRequests: [],
    streams: {},
    srtstreams: {},
    users: {},
    transcodingStats: {},
    castMachineStats: { ngcvp_machines: {}},
    cefStats: {},
    socialMediaMonitor: null,
    transcodingMachines: {},
    archiverQueue: {} as KeyDict<ArchiverTask>,
    archivingCasts: {},
    brokenArchivedCasts: {},
    wowzastats: {},
    backupOverview: new BackupOverview(),
    opsmonitorcasts: {},
    globalAccelerator: new GlobalAccelerator()
  } as OpsMonitorState,
  getters: {
    // checks if any instance in a cefMode is no longer alive
    isAlive: (state: OpsMonitorState) => (castId: string, cefMode: string = '', instanceData: CefStatDetail | undefined = undefined) : boolean => {
      const stats: CefStat | undefined = state.cefStats[castId]
      if (stats) {
        const maxAge = 60 * 1000
        const now = new Date().getTime()

        let front_alive = false
        if (stats.front) {
          if (instanceData && instanceData.cefmode === 'front') {
            // check this instance only
            front_alive = !!instanceData && !!instanceData.timestamp && (now - instanceData.timestamp.seconds * 1000 < maxAge)

          } else {
            // check all
            const front_instances = stats.front.instances || {}
            front_alive = Object.values(front_instances).length > 0 && !Object.values(front_instances).some(instance => !!instance && !!instance.timestamp && now - instance.timestamp.seconds * 1000 >  maxAge)
          }
        }

        let back_alive = false
        if (stats.back) {
          if (instanceData && instanceData.cefmode === 'back') {
            // check this instance only
            back_alive = !!instanceData && !!instanceData.timestamp && (now - instanceData.timestamp.seconds * 1000 < maxAge)

          } else {
            // check all
            const back_instances = stats.back.instances || {}
            back_alive = Object.values(back_instances).length > 0 && !Object.values(back_instances).some(instance => !!instance && !!instance.timestamp && now - instance.timestamp.seconds * 1000 >  maxAge)
          }
        }

        switch (cefMode) {
          case 'front':
            return front_alive
          case 'back':
            return back_alive
        }
        return front_alive && back_alive
      }

      return false
    },
    cefStats: (state: OpsMonitorState) => (castId: string) : CefStat | undefined => {
      return state.cefStats[castId]
    }
  },
  actions: {
    subscribeDBBackupOverview (_context: OpsMonitorContext) {
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('admin').doc('db_backup'),
        mutation: store.commit.opsmonitor.updateDBBackupOverview,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe('dbbackupoverview', snapper)
    },
    unsubscribeDBBackupOverview (_context: OpsMonitorContext) {
      storehelper.unsubscribe('dbbackupoverview')
    },
    async subscribeGlobalAccelerator (_context: OpsMonitorContext) {
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection(CollectionNames.ADMIN).doc('globalaccelerator'),
        mutation: store.commit.opsmonitor.updateGlobalAccelerator,
        type: FirestoreSnapperType.Document
      }
      await storehelper.subscribe('globalaccelerator', snapper, undefined, true)
    },
    unsubscribeGlobalAccelerator (_context: OpsMonitorContext) {
      storehelper.unsubscribe('globalaccelerator')
    },
    async subscribeWowzaStats (_context: OpsMonitorContext) {
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection('wowzastats'),
        mutation: store.commit.opsmonitor.updateWowzaStats,
        type: FirestoreSnapperType.Query
      }
      await storehelper.subscribe('wowzastats', snapper)
    },
    unsubscribeWowzaStats (_context: OpsMonitorContext) {
      storehelper.unsubscribe('wowzastats')
    },
    subscribeCefHeartbeat (_context: OpsMonitorContext, castId: string) {
      if (!castId) {
        console.warn('subscribeCefHeartbeat missing cast id')
        return
      }

      const modes = ['front','back']
      modes.forEach(cefmode => {
        const docId = castId + '_' + cefmode
        const snapper: FirestoreDocSnapper = {
          doc: fb.db.collection('cefstats').doc(docId),
          mutation: store.commit.opsmonitor.updateCefHeartbeat,
          type: FirestoreSnapperType.Document
        }
        const subscriptionId = 'cefHeartbeat' + castId + '_' + cefmode
        storehelper.subscribe(subscriptionId, snapper)
      })
    },
    unsubscribeCefHeartbeat (_context: OpsMonitorContext, castId: string) {
      const modes = ['front','back']
      modes.forEach(cefmode => {
        const subscriptionId = 'cefHeartbeat' + castId + '_' + cefmode
        storehelper.unsubscribe(subscriptionId)
      })
    },
    subscribeSRTStreams (_context: OpsMonitorContext) {
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection('streams').where('srt_state', '>', ''),
        mutation: store.commit.opsmonitor.updateSRTStreams,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe('srtstreams', snapper)
    },
    unsubscribeSRTStreams (_context: OpsMonitorContext) {
      storehelper.unsubscribe('srtstreams')
    },
    subscribeSocialMediaMonitor (_context: OpsMonitorContext) {
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('admin').doc('twitter'),
        mutation: store.commit.opsmonitor.updateSocialMediaMonitor,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe('socialmediamonitor', snapper)
    },
    unsubscribeSocialMediaMonitor (_context: OpsMonitorContext) {
      storehelper.unsubscribe('socialmediamonitor')
    },
    subscribeArchiverQueue (_context: OpsMonitorContext) {
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('admin').doc('mp4create'),
        mutation: store.commit.opsmonitor.updateArchiverQueue,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe('archiverqueue', snapper)
      const brokensnapper: FirestoreQuerySnapper = {
        query: fb.db.collection('casts')
                    .where('status', '==', 'archived')
                    .where('start_date', '>', new Date(Date.now() - 48 * 3600 * 1000)),
        mutation: store.commit.opsmonitor.updateBrokenArchivedCasts,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe('brokenarchivedcasts', brokensnapper)
    },
    unsubscribeArchiverQueue (_context: OpsMonitorContext) {
      storehelper.unsubscribe('archiverqueue')
      storehelper.unsubscribe('brokenarchivedcasts')
    },
    subscribeArchiverCast (_context: OpsMonitorContext, castid: string) {
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('casts').doc(castid),
        mutation: store.commit.opsmonitor.updateArchiverCast,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe(`archivercast_${castid}`, snapper)
    },
    unsubscribeArchiverCast (_context: OpsMonitorContext, castid: string) {
      storehelper.unsubscribe(`archivercast_${castid}`)
    },
    async requestRearchive (_context: OpsMonitorContext, castid: string) {
      const cast = await fb.db.collection('casts').doc(castid).get()
      const cast_data = cast.data()
      if (cast_data === undefined) return
      const entry = {}
      entry[`full${cast_data.k360_event_id}`] = {
            k360event: cast_data.k360_event_id,
            mode: 'full'
      }
      if (cast_data.record_isos === true) {
        entry[`ingests_${cast_data.k360_event_id}`] = {
            k360event: cast_data.k360_event_id,
            mode: 'full',
            stream: 'ingestcc'
          }
      }
      const data = {
        [cast.id]: entry 
      }
      await fb.db.collection('admin').doc('mp4create').set(data, { merge: true })
    },
    async addGlobalAcceleratorMapping (_context: OpsMonitorContext, { gaUrl, wrtcUrl}: { gaUrl: string, wrtcUrl: string }) {
      await fb.db.collection(CollectionNames.ADMIN).doc('globalaccelerator').set({
        mapping: {
          [gaUrl]: wrtcUrl
        }
      }, { merge: true })
    },
    async removeGlobalAcceleratorMapping (_context: OpsMonitorContext, gaUrl: string) {
      await fb.db.collection(CollectionNames.ADMIN).doc('globalaccelerator').set({
        mapping: {
          [gaUrl]: firestore.FieldValue.delete()
        }
      }, { merge: true })
    },
    async updateSocialMediaMonitorSettings (_context: OpsMonitorContext, settings: Partial<SocialMediaStats>) {
      await fb.db.collection('admin').doc('twitter').set(settings, { 'merge': true })
    },
    async subscribePlatinumSupportRequests (context: OpsMonitorContext) {
      const { commit } = opsMonitorModuleActionContext(context)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection('casts')
                 .where('pendingPlatinumRequest', '==', true)
                 .limit(75),
        mutation: commit.updateSupportRequests,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe('platinumrequests', snapper)
    },
    async unsubscribePlatinumSupportRequests (_context: OpsMonitorContext) {
      storehelper.unsubscribe('platinumrequests')
    },
    async subscribeOpsMonitorCasts (context: OpsMonitorContext, filter: CastStatus) {
      const { commit, dispatch } = opsMonitorModuleActionContext(context)
      Object.values(CastStatus).forEach(status => {
        if (status === filter) return
        if (!storehelper.exists(`opsmonitorcasts_${status}`)) return
        dispatch.unsubscribeOpsMonitorCasts(status)
      })
      let casts = fb.db.collection('casts').orderBy('start_date', 'desc')
      if (filter === CastStatus.Scheduled) {
        const now = new Date()
        casts = casts.where('start_date', '>', now)
      } else {
        casts = casts.where('status', '==', filter)
      }
      const snapper: FirestoreQuerySnapper = {
        query: casts.limit(75),
        mutation: commit.updateOpsMonitorCasts,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe(`opsmonitorcasts_${filter}`, snapper)
    },
    async unsubscribeOpsMonitorCasts (_context: OpsMonitorContext, filter: CastStatus) {
      storehelper.unsubscribe(`opsmonitorcasts_${filter}`)
    },
    async subscribeCastMachineStats (context: OpsMonitorContext, castId: string) {
      const { commit } = opsMonitorModuleActionContext(context)
      const snapper: FirestoreDocSnapper = {
        doc: fb.db.collection('ngcvpstats').doc(castId),
        mutation: commit.updateCastMachineStats,
        type: FirestoreSnapperType.Document
      }
      storehelper.subscribe('castmachinestats', snapper)
    },
    async unsubscribeCastMachineStats (_context: OpsMonitorContext) {
      storehelper.unsubscribe('castmachinestats')
    },
    async subscribeTranscodingStats (context: OpsMonitorContext) {
      const { commit } = opsMonitorModuleActionContext(context)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection('transcodingstats'),
        mutation: commit.updateTranscodingStats,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe('transcodingstats', snapper)
    },
    async unsubscribeTranscodingStats (_context: OpsMonitorContext) {
      storehelper.unsubscribe('transcodingstats')
    },
    async subscribeTranscodingMachines (context: OpsMonitorContext) {
      const { commit } = opsMonitorModuleActionContext(context)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection('transcodingmachines'),
        mutation: commit.updateTranscodingMachines,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe('transcodingmachines', snapper)
    },
    async unsubscribeTranscodingMachines (_context: OpsMonitorContext) {
      storehelper.unsubscribe('transcodingmachines')
    },
    async subscribeStreams (context: OpsMonitorContext) {
      const { commit } = opsMonitorModuleActionContext(context)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection('streams').where('deleted', '==', false),
        mutation: commit.updateStreams,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe('opsstreams', snapper)
    },
    async unsubscribeStreams (_context: OpsMonitorContext) {
      storehelper.unsubscribe('opsstreams')
    },
    async subscribeUsers (context: OpsMonitorContext) {
      const { commit } = opsMonitorModuleActionContext(context)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection('profiles'),
        mutation: commit.updateUsers,
        type: FirestoreSnapperType.Query
      }
      storehelper.subscribe('opsusers', snapper)
    },
    async unsubscribeUsers (_context: OpsMonitorContext) {
      storehelper.unsubscribe('opsusers')
    },
  } as const,
  mutations: {
    updateSupportRequests (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      const requests: any[] = []
      snap.forEach(request => {
        requests.push(request.data())
      })
      Vue.set(state, 'pendingSupportRequests', requests)
    },
    updateOpsMonitorCasts (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      const receivedKeys: string[] = []
      snap.forEach(cast => {
        const data = cast.data() as Cast
        const deleted = data.deleted
        receivedKeys.push(cast.id)
        if (!deleted) {
          lazyUpdate(state.opsmonitorcasts, cast.id, data)
        }
      })
      Object.keys(state.opsmonitorcasts).forEach((castId) => {
        if (!receivedKeys.includes(castId)) {
          Vue.delete(state.opsmonitorcasts, castId)
        }
      })
    },
    updateCastMachineStats (state: OpsMonitorState, doc: firestore.DocumentSnapshot) {
      if (!doc.exists) {
        return
      }
      const docData = doc.data()
      const stats = docData as  ({ [ngcvpId: string]: NGCVPMachineStat })
      Vue.set(state, 'castMachineStats', stats)
    },
    updateTranscodingStats (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      state.transcodingStats = {}
      snap.forEach(transcoder => {
        state.transcodingStats[transcoder.id] = transcoder.data() as TranscodingStats
      })
    },
    updateTranscodingMachines (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      state.transcodingMachines = {}
      snap.forEach(transcoder => {
        state.transcodingMachines[transcoder.id] = transcoder.data() as TranscodingMachine
      })
    },
    updateStreams (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      snap.forEach(streamDoc => {
        const data = { ...streamDoc.data(), id: streamDoc.id } as Stream
        if (!state.streams[streamDoc.id]) {
          state.streams[streamDoc.id] = (data)
        } else {
          lazyUpdate(state.streams, streamDoc.id, data)
        }
      })
    },
    updateUsers (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      snap.forEach(userDoc => {
        const data = userDoc.data() as UserProfile
        lazyUpdate(state.users, userDoc.id, data)
      })
    },
    updateSocialMediaMonitor (state: OpsMonitorState, snap: firestore.DocumentSnapshot) {
      const socialmonitor = snap.data()
      if (socialmonitor) {
        state.socialMediaMonitor = socialmonitor as SocialMediaStats
      }
    },
    updateArchiverQueue (state: OpsMonitorState, snap: firestore.DocumentSnapshot) {
      const archiveQueue = snap.data()
      if (archiveQueue) {
        Vue.set(state, 'archiverQueue', archiveQueue)
      }
      for (const key in state.archiverQueue) {
        if (!storehelper.exists(`archivercast_${key}`)) {
          store.dispatch.opsmonitor.subscribeArchiverCast(key)
        }
      }
      for (const key of storehelper.runningKeys('archivercast_')) {
        const castid = key.split('archivercast_')[1]
        if (!Object.keys(state.archiverQueue).includes(castid)) {
          store.dispatch.opsmonitor.unsubscribeArchiverCast(castid)
        }
      }
    },
    updateArchiverCast (state: OpsMonitorState, snap: firestore.DocumentSnapshot) {
      const cast = snap.data()
      if (cast) {
        Vue.set(state.archivingCasts, snap.id, cast)
      }
    },
    updateBrokenArchivedCasts (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      const newlist: string[] = []
      snap.forEach(doc => {
        const castid = doc.id
        const cast = doc.data()
        if (cast.mp4 === undefined && cast.deleted !== true && cast.video_start_time !== undefined) {
          newlist.push(castid)
          Vue.set(state.brokenArchivedCasts, castid, cast)
        }
      })
      for (const key in state.brokenArchivedCasts) {
        if (!newlist.includes(key)) {
          Vue.delete(state.brokenArchivedCasts, key)
        }
      }
    },
    updateSRTStreams (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      const newlist: string[] = []
      snap.forEach(doc => {
        const streamid = doc.id
        newlist.push(streamid)
        const srtstream = doc.data()
        Vue.set(state.srtstreams, streamid, srtstream)
      })
      for (const key in state.srtstreams) {
        if (!newlist.includes(key)) {
          Vue.delete(state.srtstreams, key)
        }
      }
    },
    updateCefHeartbeat (state: OpsMonitorState, snap: firestore.DocumentSnapshot) {
      const data: CefStatDetail = snap && snap.data() as CefStatDetail
      if (data && data.castId && data.cefmode) {
        // console.warn ('updateCefHeartbeat | Received heartbeat update: ' , data)
        if (state.cefStats[data.castId] === undefined) {
          state.cefStats[data.castId] = {
            castId: data.castId,
            front: {
              castId: data.castId,
              cefmode: 'front',
              instances: {}
            },
            back: {
              castId: data.castId,
              cefmode: 'back',
              instances: {}
            }
          }
        }
        lazyUpdate(state.cefStats[data.castId], data.cefmode, { ...data })
      } else {
        // console.warn ('updateCefHeartbeat | No data yet: ' , data)
      }
    },
    updateDBBackupOverview (state: OpsMonitorState, snap: firestore.DocumentSnapshot) {
      if (snap.exists) {
        lazyUpdate(state, 'backupOverview', snap.data())
      }
    },
    updateGlobalAccelerator (state: OpsMonitorState, snap: firestore.DocumentSnapshot) {
      if (snap.exists) {
        state.globalAccelerator = new GlobalAccelerator(snap.data())
      }
    },
    updateWowzaStats (state: OpsMonitorState, snap: firestore.QuerySnapshot) {
      state.wowzastats = {}
      snap.forEach(doc => {
        Vue.set(state.wowzastats, doc.id, new WowzaStat(doc.data()))
        const server = 'wrtc-' + doc.id + '.kiswe.com'
        Object.entries(state.globalAccelerator.mapping).forEach(([ga, wrtc]) => {
          if (wrtc === server) {
            const gaId = ga.replace('wrtc-', '').replace('.kiswe.com', '')
            Vue.set(state.wowzastats, gaId, new WowzaStat(doc.data()))
          }
        })
      })
    }
  }
} as const

export default OpsMonitorModule
export const opsMonitorModuleActionContext = context => moduleActionContext(context, OpsMonitorModule)
